import { useState, useEffect } from 'react';
import { getSampleDecisionResult } from '../api/decisionEngine';
import type { DecisionEngineResult } from '../api/decisionEngine';
import { BaselineKPITiles } from '@system-rec/decision-ui';
import type { BaselineKPIData } from '@system-rec/decision-ui';

function BaselinePage() {
  const [result, setResult] = useState<DecisionEngineResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSampleDecisionResult()
      .then(setResult)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="page baseline-page">
        <div className="page-header">
          <h2>Baseline Cost Analysis</h2>
          <p>Loading baseline data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page baseline-page">
        <div className="page-header">
          <h2>Baseline Cost Analysis</h2>
          <p className="error">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  // Check if baseline is enabled in the input
  const baselineEnabled = result?.normalizedInput?.baselineEnabled ?? true;

  // If baseline is disabled, show the CTA card
  if (!baselineEnabled) {
    return (
      <div className="page baseline-page">
        <div className="page-header">
          <h2>Baseline Cost Analysis</h2>
          <p className="page-subtitle">
            Current system performance and cost projection over time
          </p>
        </div>
        <section className="baseline-kpis">
          <BaselineKPITiles data={null} baselineEnabled={false} />
        </section>
      </div>
    );
  }

  // If baseline is enabled but no result, show unavailable state
  if (!result || !result.output.baselineResult) {
    return (
      <div className="page baseline-page">
        <div className="page-header">
          <h2>Baseline Cost Analysis</h2>
          <p className="page-subtitle">
            Current system performance and cost projection over time
          </p>
        </div>
        <section className="baseline-kpis">
          <BaselineKPITiles data={null} baselineEnabled={true} />
        </section>
      </div>
    );
  }

  const { output, costOverTimeData } = result;

  // Map baseline result to KPI data format
  const kpiData: BaselineKPIData = {
    annualCost_gbp: output.baselineResult.annualizedCost_gbp,
    totalCost_gbp: output.baselineResult.totalCost_gbp,
    periodYears: output.baselineResult.periodYears,
    remainingLifespan_years: output.baselineResult.longevity?.remainingLifespan_years || null,
    expectedReplacements: output.baselineResult.longevity?.expectedReplacementsInPeriod || null,
    efficiencySource: output.baselineResult.efficiencySource,
    sludgeRiskApplied: output.baselineResult.hydronicsInfo?.sludgeRiskApplied || false,
    sludgeRiskLevel: output.baselineResult.hydronicsInfo?.severity,
  };

  return (
    <div className="page baseline-page">
      <div className="page-header">
        <h2>Baseline Cost Analysis</h2>
        <p className="page-subtitle">
          Current system performance and cost projection over time
        </p>
      </div>

      <section className="baseline-kpis">
        <BaselineKPITiles data={kpiData} />
      </section>

      {costOverTimeData && (
        <section className="baseline-chart">
          <h3>Cost Projection Over Time</h3>
          <div className="chart-info">
            <p>This shows how your costs accumulate over {costOverTimeData.metadata.periodYears} years.</p>
          </div>
        </section>
      )}

      <section className="baseline-summary">
        <div className="info-card">
          <h4>About This Analysis</h4>
          <p>
            This baseline represents the "do nothing" scenario - keeping your current 
            system and projecting costs over the next {output.baselineResult.periodYears} years. It includes energy costs, 
            maintenance, and expected repairs based on your system's age and condition.
          </p>
          <p>
            Use this as a comparison point when evaluating new system options in the 
            Systems and Decision screens.
          </p>
        </div>
      </section>
    </div>
  );
}

export default BaselinePage;
